import { useEffect, useRef, useState } from 'react'
import { useSimStore } from '../store/simStore'

/** Smoothly interpolates sim time using requestAnimationFrame.
 *  Never lets the displayed value go backwards (clamps to previous max). */
export function useSimClock(): number {
  const simTime = useSimStore((s) => s.simTime)
  const appState = useSimStore((s) => s.appState)
  const rtFactor = useSimStore((s) => s.rtFactor)

  const [display, setDisplay] = useState(simTime)
  const lastRealRef  = useRef<number>(performance.now())
  const lastSimRef   = useRef<number>(simTime)
  const maxShownRef  = useRef<number>(simTime)   // never show time going backwards
  const rafRef       = useRef<number>(0)

  // When a new authoritative simTime arrives, anchor interpolation from here
  useEffect(() => {
    // Only advance the anchor; never let it pull display backwards
    if (simTime >= lastSimRef.current) {
      lastSimRef.current  = simTime
      lastRealRef.current = performance.now()
    }
  }, [simTime])

  // Reset max when a new run begins (appState goes to LOBBY then back)
  useEffect(() => {
    if (appState === 'LOBBY') {
      maxShownRef.current = 0
      lastSimRef.current  = 0
    }
  }, [appState])

  useEffect(() => {
    if (appState !== 'RUNNING' || rtFactor <= 0) {
      // Full-speed: display exactly what the backend reports, but never backward
      setDisplay((prev) => Math.max(prev, simTime))
      return
    }
    const tick = () => {
      const elapsed = (performance.now() - lastRealRef.current) / 1000
      // rtFactor = real seconds per sim-minute (SimPy RealtimeEnvironment.factor)
      // elapsed real seconds → elapsed / rtFactor sim-minutes
      const interpolated = lastSimRef.current + elapsed / rtFactor
      const safe = Math.max(maxShownRef.current, interpolated)
      maxShownRef.current = safe
      setDisplay(safe)
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [appState, rtFactor])  // simTime excluded: anchor is managed via refs in the effect above

  return display
}
